import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import {
  FaFileUpload,
  FaTrash,
  FaDownload,
  FaCheckCircle,
  FaTimesCircle,
  FaCommentAlt,
} from 'react-icons/fa';
import { AuthContext } from '../context/AuthContext';

/** Certificados JSON: solo admin/inspector (no rol user) suben; mismo criterio que Anexo 30 y 31 2024/2025 */
const CERTIFICATE_TITLE_IDS = [348, 349, 409, 450, 492, 510];

const getDocumentBackgroundClass = (status) => {
  switch (status) {
    case 'pending':
      return 'border-l-4 border-yellow-400 bg-yellow-50';
    case 'complete':
      return 'border-l-4 border-green-400 bg-green-50';
    case 'incomplete':
      return 'border-l-4 border-red-400 bg-red-50';
    default:
      return 'border-l-4 border-gray-200';
  }
};

const getDocumentStatusIcon = (status) => {
  switch (status) {
    case 'complete':
      return <FaCheckCircle className="text-green-500" />;
    case 'incomplete':
      return <FaTimesCircle className="text-red-500" />;
    default:
      return null;
  }
};

/** Comentarios en orden cronológico (más antiguo primero). */
const sortCommentsByCreatedAt = (comments) => {
  if (!Array.isArray(comments)) return [];
  return [...comments].sort((a, b) => {
    const ta = new Date(a.created_at).getTime();
    const tb = new Date(b.created_at).getTime();
    return ta - tb;
  });
};

const TitleList = ({
  titles,
  onDocumentUpload,
  onDocumentDelete,
  onCommentSubmit,
  onCommentChange,
  newComments,
  onCommentDelete,
  handleChangeDocumentStatus,
}) => {
  const { authTokens } = useContext(AuthContext);
  const userRole = authTokens?.userRole;
  const [isProcessing, setIsProcessing] = useState(false);
  /** Solo la tarjeta bajo el cursor muestra resaltado al arrastrar */
  const [dragActiveTitleId, setDragActiveTitleId] = useState(null);

  const handleStatusChange = async (titleId, documentId, newStatus) => {
    if (isProcessing) return;
    setIsProcessing(true);
    try {
      await handleChangeDocumentStatus(titleId, documentId, newStatus);
    } catch (error) {
      console.error('Error al cambiar el estado del documento:', error);
    } finally {
      setTimeout(() => {
        setIsProcessing(false);
      }, 500);
    }
  };

  const handleDragEnter = (e, titleId) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActiveTitleId(titleId);
  };

  const handleDragOver = (e, titleId) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActiveTitleId(titleId);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.relatedTarget && e.currentTarget.contains(e.relatedTarget)) return;
    setDragActiveTitleId(null);
  };

  const handleDrop = (e, titleId) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActiveTitleId(null);
    const { files } = e.dataTransfer;
    if (!files || files.length === 0) return;
    onDocumentUpload({ target: { files } }, titleId);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {titles.length > 0 ? (
        titles.map((title) => {
          const sortedComments = sortCommentsByCreatedAt(title.comments);
          return (
            <div
              key={title.id}
              className="bg-white rounded-lg shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden"
            >
              <div className="p-4 border-b bg-gray-50">
                <h2
                  className="text-md font-medium text-gray-800"
                  style={{
                    height: '11vh',
                    overflowY: 'auto',
                    scrollbarWidth: 'thin',
                    scrollbarColor: '#4B5563 #E5E7EB',
                  }}
                >
                  {title.title_name}
                </h2>
              </div>

              <div className="p-4">
                {/* Document Upload Section */}
                {(userRole !== 'user' || !CERTIFICATE_TITLE_IDS.includes(title.id)) && (
                <div
                  className={`
                    relative border-2 border-dashed rounded-lg p-4 mb-4 transition-colors
                    ${dragActiveTitleId === title.id ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400'}
                  `}
                  onDragEnter={(e) => handleDragEnter(e, title.id)}
                  onDragLeave={handleDragLeave}
                  onDragOver={(e) => handleDragOver(e, title.id)}
                  onDrop={(e) => handleDrop(e, title.id)}
                >
                  <input
                    type="file"
                    onChange={(e) => onDocumentUpload(e, title.id)}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                    title="Subir documento"
                  />
                  <div className="text-center">
                    <FaFileUpload className="mx-auto text-3xl text-gray-400 mb-2" />
                    <p className="text-sm text-gray-500">
                      Arrastra un archivo o haz clic para seleccionar
                    </p>
                  </div>
                </div>
                )}

                {/* Documents List */}
                <div className="space-y-3">
                  {title.documents && title.documents.length > 0 ? (
                    title.documents.map((document) => (
                      <div
                        key={document.id}
                        className={`
                        ${getDocumentBackgroundClass(document.status)}
                        p-3 rounded-lg transition-all duration-200 hover:shadow-sm
                      `}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="text-sm font-medium text-gray-700">
                              {document.document_title}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              {new Date(document.created_at).toLocaleString()}
                            </p>
                          </div>
                          <div className="flex items-center space-x-2">
                            {getDocumentStatusIcon(document.status)}
                          </div>
                        </div>

                        <div className="mt-3 flex items-center space-x-3">
                          <a
                            href={document.file_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            download
                            className="inline-flex items-center text-xs text-blue-600 hover:text-blue-800"
                          >
                            <FaDownload className="mr-1" />
                            Descargar
                          </a>
                          {(userRole === 'admin' || userRole === 'super_admin') && (
                          <>
                            <button
                              type="button"
                              onClick={() => handleStatusChange(title.id, document.id, 'complete')}
                              className="inline-flex items-center text-xs text-green-600 hover:text-green-800"
                              disabled={isProcessing}
                            >
                              <FaCheckCircle className="mr-1" />
                              Completo
                            </button>
                            <button
                              type="button"
                              onClick={() => handleStatusChange(title.id, document.id, 'incomplete')}
                              className="inline-flex items-center text-xs text-red-600 hover:text-red-800"
                              disabled={isProcessing}
                            >
                              <FaTimesCircle className="mr-1" />
                              Incompleto
                            </button>
                          </>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              if (window.confirm('¿Estás seguro de que quieres eliminar este documento?')) {
                                onDocumentDelete(title.id, document.id);
                              }
                            }}
                            className="inline-flex items-center text-xs text-gray-600 hover:text-gray-800"
                            disabled={isProcessing}
                          >
                            <FaTrash className="mr-1" />
                            Eliminar
                          </button>
                        </div>
                      </div>
                    ))
                  ) : (
                    userRole === 'user' && CERTIFICATE_TITLE_IDS.includes(title.id) && (
                    <p className="text-sm text-gray-500 text-center py-2">El inspector aun no ha subido tu certificado</p>
                    )
                  )}
                </div>

                {/* Comments Section */}
                {(userRole !== 'user' || !CERTIFICATE_TITLE_IDS.includes(title.id)) && (
                  <div className="mt-4 pt-4 border-t">
                    <div className="flex items-center mb-3">
                      <FaCommentAlt className="text-gray-400 mr-2" />
                      <h3 className="text-sm font-medium text-gray-700">Comentarios</h3>
                    </div>
                    <form onSubmit={(e) => onCommentSubmit(e, title.id)} className="space-y-2">
                      <textarea
                        value={newComments[title.id] || ''}
                        onChange={(e) => onCommentChange(e, title.id)}
                        className={
                          'w-full resize-none rounded-lg border p-2 text-sm text-gray-700 '
                          + 'focus:border-transparent focus:ring-2 focus:ring-blue-500'
                        }
                        rows="2"
                        placeholder="Escribe un comentario..."
                      />
                      <button
                        type="submit"
                        className={
                          'w-full rounded-lg bg-blue-500 py-2 px-4 text-sm font-medium text-white '
                          + 'transition-colors duration-200 hover:bg-blue-600'
                        }
                        disabled={isProcessing}
                      >
                        Enviar Comentario
                      </button>
                    </form>

                    <div className="mt-3">
                      {sortedComments.length > 0 ? (
                        <div
                          className={
                            'max-h-72 space-y-2 overflow-y-auto pr-1 '
                            + 'scrollbar-thin scrollbar-track-gray-100 scrollbar-thumb-gray-300'
                          }
                        >
                          {sortedComments.map((comment) => {
                            const authorName = typeof comment.author_name === 'string' ? comment.author_name.trim() : '';
                            return (
                              <div key={comment.id} className="bg-gray-50 rounded-lg p-3">
                                {authorName ? (
                                  <div className="mb-2 flex items-center gap-3">
                                    <div
                                      className={
                                      'flex h-9 w-9 shrink-0 items-center justify-center rounded-full '
                                      + 'bg-gradient-to-br from-blue-500 to-indigo-600 text-sm font-bold '
                                      + 'uppercase text-white shadow-md ring-2 ring-white'
                                    }
                                      aria-hidden
                                    >
                                      {authorName.charAt(0)}
                                    </div>
                                    <p className="min-w-0 flex-1 truncate text-sm font-semibold leading-snug text-gray-900">
                                      {authorName}
                                    </p>
                                  </div>
                                ) : null}
                                <p className="text-sm text-gray-700">{comment.content}</p>
                                <div className="flex items-center justify-between mt-2">
                                  <p className="text-xs text-gray-500">
                                    {new Date(comment.created_at).toLocaleString()}
                                  </p>
                                  {userRole === 'super_admin' && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      if (window.confirm('¿Estás seguro de que quieres eliminar este comentario?')) {
                                        onCommentDelete(title.id, comment.id);
                                      }
                                    }}
                                    className="text-xs text-red-500 hover:text-red-700"
                                    disabled={isProcessing}
                                  >
                                    Eliminar
                                  </button>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 text-center py-2">No hay comentarios aún</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          );
        })
      ) : (
        <p className="col-span-3 text-center text-gray-500">Cargando requerimientos...</p>
      )}
    </div>
  );
};

TitleList.propTypes = {
  titles: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    title_name: PropTypes.string.isRequired,
    documents: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      document_title: PropTypes.string,
      created_at: PropTypes.string.isRequired,
    })).isRequired,
    comments: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number.isRequired,
      content: PropTypes.string.isRequired,
      created_at: PropTypes.string.isRequired,
      author_name: PropTypes.string,
    })).isRequired,
  })).isRequired,
  onDocumentUpload: PropTypes.func.isRequired,
  onDocumentDelete: PropTypes.func.isRequired,
  handleChangeDocumentStatus: PropTypes.func.isRequired,
  onCommentSubmit: PropTypes.func.isRequired,
  onCommentChange: PropTypes.func.isRequired,
  onCommentDelete: PropTypes.func.isRequired,
  newComments: PropTypes.objectOf(PropTypes.string).isRequired,
};

export default TitleList;
